<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">
                <chevron
                  @click="$router.push({name: 'Settings'})"/>
                {{ $t('group.title') }}
              </h1>
              <div class="header-add-button">
                <router-link to="/group">
                  <span
                    v-tooltip.left.top-start="$t('group.add')">
                    <font-awesome-icon
                      class="header-add-button-icon"
                      :icon="icon.add"/>
                  </span>
                </router-link>
              </div>
            </div>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="groupTable"
              :data="fetchData"
              sort-by="name"
              sort-order="asc"
              :show-caption="false"
              class="datatable"
              filter-placeholder=""
              filterNoResults="">
              <table-column
                show="name"
                :label="$t('group.name')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-has-route="'/group/permission/list'"
                    v-on:click="onPermissions(row)"
                    :icon="icon.key"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-if="row.parentId !== null"
                    :id="row"
                    v-on:click="onUsers(row)"
                    :icon="icon.users"
                    class="table-iconset-icon"/>
                  <font-awesome-icon
                    v-else
                    :id="row"
                    :icon="icon.users"
                    class="table-icon-disabled"/>
                </template>
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    :id="row"
                    v-on:click="onUpdate(row)"
                    :icon="icon.pencil"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-if="row.parentId !== null"
                    v-on:click="onDelete(row)"
                    :icon="icon.trash"
                    class="table-iconset-icon"/>
                  <font-awesome-icon
                    v-else
                    :icon="icon.trash"
                    class="table-icon-disabled"/>
                </template>
              </table-column>
            </table-component>
          </grid-col>
        </grid-row>
      </div>
      <ok-cancel-modal
        v-if="showConfirmationDeleteModal"
        v-on:ok="onDeleteConfirm"
        v-on:cancel="onDeleteCancel"
        :title="deleteModalMessage"
        :message="deleteModalMessage"
        :okButton="$t('commons.delete')"
        :cancelButton="$t('commons.cancel')">
        <div slot="body">
          <ul>
            <li>
              {{ groupToDelete.name }}
            </li>
          </ul>
        </div>
      </ok-cancel-modal>
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '../MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import pagination from '@/components/shared/table/pagination';
import {mapActions, mapGetters} from 'vuex';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faKey,
  faPencilAlt,
  faTrashAlt,
  faUsers
} from '@fortawesome/pro-regular-svg-icons';
import {faPlusCircle} from '@fortawesome/pro-solid-svg-icons';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import Alert from '@/components/shared/alert/Alert';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import BtnPill from '@/components/shared/buttons/BtnPill';
import Chevron from '@/components/shared/Chevron';

export default {
  components: {
    MainLayout,
    GridRow,
    GridCol,
    TableComponent,
    TableColumn,
    FontAwesomeIcon,
    OkCancelModal,
    Alert,
    BtnPill,
    Chevron
  },
  data () {
    return {
      alertVisible: false,
      alertMessage: '',
      alertType: '',
      groupToDelete: undefined,
      showConfirmationDeleteModal: false,
      deleteModalMessage: '',
      icon: {
        add: faPlusCircle,
        trash: faTrashAlt,
        pencil: faPencilAlt,
        users: faUsers,
        key: faKey
      },
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      }
    };
  },
  watch: {
    getGroups: {
      handler: function () {
        this.$refs.groupTable.refresh();
      }
    }
  },
  created () {
    this.retrieveGroups({context: this});

    const alert = this.$route.params.alert;
    if (alert !== undefined) {
      showAlert(this, this.$t(alert), 'success');
    }
  },
  computed: {
    getBreadcrumb () {
      return [
        {
          link: '/settings',
          name: this.$t('menu.settings')
        },
        {
          link: '/group/list',
          name: this.$t('group.title'),
          disabled: true
        }
      ];
    },
    ...mapGetters([
      'getGroups'
    ])
  },
  methods: {
    fetchData ({ page, filter, sort }) {
      const tablePage = pagination.createPage(
        this.getGroups,
        this.tableState,
        ['name'],
        this.tablePageSize,
        { page, filter, sort });
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    onUpdate (group) {
      this.$router.push({ name: 'Group', params: group });
    },
    onUsers (group) {
      this.$router.push({ name: 'GroupUserList', params: group });
    },
    onDelete (group) {
      this.groupToDelete = group;
      this.deleteModalMessage = this.$t('group.deleteModalMessage');
      this.showConfirmationDeleteModal = true;
    },
    onDeleteConfirm () {
      const id = this.groupToDelete.id;
      const groupName = this.groupToDelete.name;
      this.groupToDelete = {};
      this.showConfirmationDeleteModal = false;
      this.deleteGroup({ context: this, id: id }).then(() => {
        showAlert(this, this.$t('group.successDelete', {name: groupName}), 'success');
      }, () => {
        showAlert(this, this.$t('group.errorDelete', {name: groupName}), 'error');
      });
    },
    onDeleteCancel () {
      this.groupToDelete = {};
      this.showConfirmationDeleteModal = false;
    },
    onPermissions (group) {
      this.$router.push({ name: 'GroupPermissionList', params: group });
    },
    ...mapActions([
      'retrieveGroups',
      'deleteGroup'
    ])
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};
</script>

<style lang="scss" >
  @import "src/stylesheets/variables";

  #header {
    display: inline-block;
    width: 100%;
    line-height: 1rem;
  }

  .header-title {
    float: left;
  }

  .header-add-button {
    float: right;
  }

  .header-add-button-icon {
    font-size: 3rem;
    color: $secondary;
  }

  .table-iconset-icon {
    margin: 0 0.5em;
    cursor: pointer;
  }

  .table-icon-disabled {
    opacity: 0.4;
    margin: 0 0.5em;
    cursor: default;
  }

  .table-component {
    $filter-padding: $sp-margin-smaller;

    &__th--sort,
    &__th--sort-asc,
    &__th--sort-desc {
      cursor: pointer;
      position: relative;
      padding-right: 1rem !important;

      &::before,
      &::after {
        position: absolute;
        font-size: 0.3rem;
        right: 0.3rem;
        opacity: 0.5;
      }

      &:before {
        top: 0.7rem;
      }

      &:after {
        bottom: 0.6rem;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/layouts/groups/GroupList.vue